import { useCallback, useMemo } from 'react';
import { CLIP_UPDATE_TYPE } from '../../../../clipUpdateDataUtils/constants';
import { updateClipData } from '../../../../clipUpdateDataUtils';
import { Scene } from '@/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { SpeakerWithDetails } from '@/player-config/src/types/speaker-types';
import { ORIGINAL_VIDEO_ID } from '@/player-config/src/constants/screenshare';
import { isLayoutDefault } from '@/player-config/src/utils/layout';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { selectSpeakerDetails, selectVideoFeeds } from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { buildSceneTracks } from '@/stores/contentMetaDataStore/utils/sceneTracks';

export { SCREENSHARE_TRACK_NAME, SHARED_VIDEO_TRACK_NAME } from '@/stores/contentMetaDataStore/utils/sceneTracks';

export type SpeakerTimelineTrack = Pick<SpeakerWithDetails, 'bgColor' | 'lightBgColor' | 'color'> & {
  id: string;
  name: string;
  toggleKey: string;
  selected: boolean;
};

export const getTracksForScene = (scene: Scene, tracks: SpeakerTimelineTrack[]) => {
  if (isLayoutDefault(scene.details.layout)) {
    return tracks.filter(track => track.id === ORIGINAL_VIDEO_ID);
  }
  return tracks.filter(track => track.id !== ORIGINAL_VIDEO_ID);
};

export function useSceneTracks({
  broadcastId,
  clipId,
  sceneId,
  scenes,
  compositeSegmentId
}: {
  broadcastId: string;
  clipId: string;
  sceneId: string;
  scenes: Scene[];
  compositeSegmentId: string;
}) {
  const currentScene = useMemo(() => scenes.find(scene => scene.id === sceneId), [scenes, sceneId]);
  const speakerDetails = useContentMetadataStore(selectSpeakerDetails(broadcastId));
  const videoFeeds = useContentMetadataStore(selectVideoFeeds(broadcastId));

  const tracksToRender = useMemo(() => {
    const allTracks = buildSceneTracks({ scene: currentScene ?? undefined, videoFeeds, speakerDetails });

    if (!currentScene) {
      return allTracks.filter(track => track.id === ORIGINAL_VIDEO_ID);
    }

    return getTracksForScene(currentScene, allTracks);
  }, [currentScene, videoFeeds, speakerDetails]);

  const handleTrackToggle = useCallback(
    (toggledTrackKey: string) => {
      if (!currentScene) {
        return;
      }

      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.TOGGLE_VIDEO_VISIBILITY_FROM_SCENE,
        sceneId: currentScene.id,
        trackName: toggledTrackKey,
        compositeId: compositeSegmentId
      });
    },
    [clipId, currentScene, compositeSegmentId]
  );

  return {
    tracksToRender,
    handleTrackToggle
  };
}
